import type { ComplianceFiling, ComplianceTask } from '../types';
import { appLocale } from './appLocale';

/**
 * Helpers for the F1 filing-activity feed and the F2 obligation-history
 * popover. Pulled out of `StateTaxClientDetailPage.tsx` so the flatten +
 * formatting logic gets unit coverage without spinning up a render.
 */

/**
 * One filed filing pinned to its parent task, with `filedAtMs` precomputed
 * for sort comparators.
 */
export interface FlatFiling {
  task: ComplianceTask;
  filing: ComplianceFiling;
  filedAtMs: number;
}

/**
 * Flatten a client's tasks → only the filings that are actually filed,
 * sorted desc by FiledAt. Drops entries with missing or unparseable filedAt
 * timestamps so the table never shows a "Filed: —" row.
 *
 * Pure: no React, no hooks, no globals beyond Date.parse.
 */
export function flattenFiledFilings(tasks: ComplianceTask[]): FlatFiling[] {
  const out: FlatFiling[] = [];
  for (const task of tasks) {
    for (const filing of task.filings) {
      if (!filing.filedAt) continue;
      const filedAtMs = Date.parse(filing.filedAt);
      if (Number.isNaN(filedAtMs)) continue;
      out.push({ task, filing, filedAtMs });
    }
  }
  out.sort((a, b) => b.filedAtMs - a.filedAtMs);
  return out;
}

/**
 * ISO timestamp → locale "Apr 19, 2026, 9:00 AM"–style string for the F2
 * obligation-history popover. Falls back to the raw ISO string if Date.parse
 * fails so the UI never shows "Invalid Date".
 */
export function formatObligationEventTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(appLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Render a Before/After dict value for the F2 popover delta line. Booleans
 * get glyph treatment so a `hasDirectAccess` change reads as "✓ → ✗" rather
 * than "true → false". Null/undefined collapse to a dash.
 */
export function formatDeltaValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  return String(value);
}
